<template>
  <v-card-text>
    <v-container class="py-0 ma-0" fluid ref="firstContainer">
      <v-row>
        <v-col cols="10">
          <v-autocomplete
            v-model="tableFilters"
            return-object
            :items="headers.filter(x => x.text !== 'Acciones')"
            :items-text="(h) => {return h.text}"
            item-title="text"
            label="Mostrar campos"
            multiple
            clearable
            dense
            outlined
          >
            <template #selection="{ item, index }">
              <v-chip v-if="index < maxVisible" :key="item.value" small>
                {{ item.text }}
              </v-chip>
              <span v-else-if="index===maxVisible" :key="index" small>
                {{ computedHeaders.length - maxVisible - 1 }} más
              </span>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="1">
          <v-btn
            icon
            color="teal"
            @click="resetFilters()"
          >
            <v-icon>mdi-cached</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="1">
          <v-btn
            icon
            color="teal"
            @click="downloadExcelDispositivos()"
          >
            <v-icon>mdi-file-excel-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            color="red"
            @click="exportarDispositivosPDF()"
          >
            <v-icon>mdi-file-pdf-box</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Búsqueda -->
      <v-row class="mt-0">
        <v-col cols="2">
          <v-text-field
            class="no-spacing"
            v-model="searchGeneral"
            label="Búsqueda general"
            clearable
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            class="no-spacing"
            v-model="searchDispositivo"
            label="Dispositivo"
            clearable
          />
        </v-col>
        <v-col cols="2">
          <v-autocomplete
            v-model="proveedorSelected"
            :items="proveedor"
            item-text="NOMBRE"
            item-value="NOMBRE"
            label="Proveedor"
            clearable
            class="no-spacing"
          />
        </v-col>

        <!-- Nuevo: Medio (texto libre) -->
        <v-col cols="2">
          <v-text-field class="no-spacing" v-model="searchMedio" label="Medio" clearable />
        </v-col>
      </v-row>

      <!-- Tabla de dispositivos -->
      <v-row>
        <v-data-table
          class="table"
          :headers="computedHeaders"
          :items="dispositivosFiltrados"
          :search="searchGeneral"
          fixed-header
          disable-pagination
          hide-default-footer
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
        >
          <template #no-data>
            <p>Datos no disponibles.</p>
          </template>

          <template #[`item.FECHA_CREACION`]="{ item }">
            <span>{{ $date.formatDate(item.FECHA_CREACION, FMT.onlyDate) }}</span>
          </template>

          <template #[`item.ACCIONES`]="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  @click="showEditDispositivoDialog(item)"
                >
                  <v-icon color="teal">mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  @click="confirmDeleteDispositivo(item)"
                >
                  <v-icon color="red">mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Borrar</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-row>
    </v-container>

    <!-- Dialogo para añadir/editar dispositivo -->
    <vx-dialog-add-edit-dispositivo
      :show.sync="showDialog"
      :isEdit="isEdit"
      :editedDispositivo="editDispositivo"
      @cancelar="handleCancelDialog"
      @addDispositivo="handleAddDispositivo"
      @editDispositivo="handleEditDispositivo"
    />
  </v-card-text>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DialogAddEditDispositivo from '@/components/medios/DialogAddEditDispositivo.vue'
import { downloadDispositivosExcel } from '@/helpers/ExcelHelper'
import proveedor from '@/api/proveedor/proveedor'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import constants from '@/helpers/constants'
import DateService, { FMT } from '@/helpers/DateService'

pdfMake.vfs = pdfFonts.pdfMake.vfs
export default {
  components: {
    'vx-dialog-add-edit-dispositivo': DialogAddEditDispositivo
  },

  props: {
    showDialogAddEdit: Boolean
  },

  data: () => ({
    headers: [
      { text: 'Dispositivo', value: 'DISPOSITIVO', align: 'left', sortable: true, initialShow: true },
      { text: 'Proveedor', value: 'PROVEEDOR', align: 'left', sortable: true, initialShow: true },
      { text: 'Medio', value: 'MEDIO', align: 'left', sortable: true, initialShow: true },
      // { text: 'Fecha de creación', value: 'FECHA_CREACION', align: 'left', sortable: true },
      { text: 'Acciones', value: 'ACCIONES', align: 'center', width: '10%', sortable: false, initialShow: true }
    ],
    sortBy: 'DISPOSITIVO',
    sortDesc: false,
    searchGeneral: '',
    searchDispositivo: '',
    searchMedio: '',
    proveedorSelected: null,
    showDialog: false,
    isEdit: false,
    editDispositivo: {},
    tableFilters: [],
    maxVisible: 8
  }),

  watch: {
    showDialogAddEdit (val) {
      this.showDialog = val
    }
  },

  computed: {
    ...mapGetters('dispositivo', ['dispositivos', 'medio']),
    ...mapGetters('proveedor', { proveedoresLista: 'proveedores' }),
    proveedor () {
      return this.proveedoresLista || []
    },

    dispositivosFiltrados () {
      return this.dispositivos.filter(d => {
        const general = this.searchGeneral.toLowerCase()
        const dispositivo = this.searchDispositivo.toLowerCase()
        const medio = this.searchMedio.toLowerCase()
        const proveedor = this.proveedorSelected

        const matchGeneral = general
          ? (
              d.DISPOSITIVO?.toLowerCase().includes(general) ||
              d.MEDIO?.toLowerCase().includes(general) ||
              d.PROVEEDOR?.toLowerCase().includes(general)
            )
          : true

        const matchDispositivo = dispositivo
          ? d.DISPOSITIVO?.toLowerCase().includes(dispositivo)
          : true

        const matchMedio = medio
          ? d.MEDIO?.toLowerCase().includes(medio)
          : true

        const matchProveedor = proveedor
          ? d.PROVEEDOR?.toLowerCase() === proveedor.toLowerCase()
          : true

        return matchGeneral && matchDispositivo && matchMedio && matchProveedor
      })
    },

    computedHeaders () {
      return this.headers.filter(header =>
        header.text === 'Acciones' ||
      this.tableFilters.some(f => f.value === header.value)
      )
    }

  },

  methods: {
    ...mapActions('dispositivo', ['fetchDispositivos', 'addDispositivo', 'updateDispositivo', 'deleteDispositivo']),

    async handleAddDispositivo (dispositivo) {
      try {
        const { ID_MEDIO, ...dispositivoData } = dispositivo

        const res = await this.addDispositivo(dispositivoData)
        const nuevoDispositivo = {
          ID_GEO_DISPOSITIVO: res.ID_GEO_DISPOSITIVO
        }
        this.showDialog = false
        this.isEdit = false
        this.editDispositivo = {}
        await this.fetchDispositivos()
      } catch (err) {
        console.error(err)
        if (err.response?.status === 409) {
          this.$notify({ text: 'Ya existe un dispositivo con ese nombre', color: 'red' })
        } else {
          this.$notify({ text: 'Error al añadir dispositivo', color: 'red' })
        }
      }
    },

    async handleEditDispositivo (dispositivo) {
      try {
        await this.updateDispositivo(dispositivo)
        this.showDialog = false
        await this.fetchDispositivos(dispositivo)
      } catch (err) {
        this.$notify({ text: 'Error al actualizar dispositivo', color: 'red' })
        console.error(err)
      }
    },

    async confirmDeleteDispositivo (dispositivo) {
      const confirm = await this.$root.$confirmDialog.open(
        '¿Estás seguro?',
        `Vas a borrar el dispositivo: ${dispositivo.DISPOSITIVO}`
      )
      if (confirm) {
        await this.deleteDispositivo(dispositivo)
      }
    },
    async deleteDispositivo (dispositivo) {
      try {
        await this.deleteDispositivo(dispositivo.ID_GEO_DISPOSITIVO)
        await this.fetchDispositivos(dispositivo)
      } catch (err) {
        console.error('Error al borrar dispositivo:', err)
        this.$notify({ text: 'Error al borrar dispositivo', color: 'red' })
      }
    },

    showEditDispositivoDialog (item) {
      this.editDispositivo = Object.assign({}, item)
      this.isEdit = true
      this.showDialog = true
    },

    async acceptAddDispositivo (dispositivo) {
      await this.addDispositivo(dispositivo)
      this.showDialog = false
    },

    async acceptEditDispositivo (dispositivo) {
      await this.updateDispositivo(dispositivo)
      this.showDialog = false
    },

    handleCancelDialog () {
      this.showDialog = false
      this.$emit('cancelar')
    },
    async downloadExcelDispositivos () {
      const headers = this.computedHeaders.filter(h => h.value !== 'ACCIONES')
      const data = this.dispositivosFiltrados
      await downloadDispositivosExcel(headers, data, 'dispositivos.xlsx')
    },
    exportarDispositivosPDF () {
      let bodyPdf = [['Dispositivo', 'Proveedor', 'Medio']]

      let dispositivoFechaOk = this.dispositivos.filter((x) =>
        this.$date.parseDate(x.FECHA).isValid()
      )
      let dispositivoFechaMal = this.dispositivos.filter(
        (x) => !this.$date.parseDate(x.FECHA).isValid()
      )

      let dispositivosOrdenados = dispositivoFechaOk.sort((a, b) => {
        let aOrden = this.$date.parseDate(a.FECHA)
        let bOrden = this.$date.parseDate(b.FECHA)

        return bOrden > aOrden ? 1 : -1
      })

      let dispositivosOrdenadosFinal = dispositivosOrdenados.concat(dispositivoFechaMal)

      for (let i = 0; i < dispositivosOrdenadosFinal.length; i++) {
        let dispositivo = dispositivosOrdenadosFinal[i]

        let datosDispositivo = []
        datosDispositivo.push({ text: dispositivo.DISPOSITIVO, bold: true })
        datosDispositivo.push(dispositivo.PROVEEDOR)
        datosDispositivo.push(dispositivo.MEDIO)
        bodyPdf.push(datosDispositivo)
      }

      let docDefinition = {
        content: [],
        defaultStyle: {
          fontSize: 9,
          alignment: 'center'
          // image: constants.logoBase64,
        }
      }

      docDefinition.content.push(constants.headerPDF)

      docDefinition.content.push(
        {
          text: [
            { text: 'Fecha/hora informe: ', bold: true },
            this.$date.currentDate()
          ],
          alignment: 'center',
          margin: [0, 0, 0, 15]
        },
        {
          layout: 'lightHorizontalLines',
          table: {
            headerRows: 1,
            widths: ['*', '*', '*'],
            body: bodyPdf
          },
          // alignment: 'center',
          margin: [0, 15, 0, 20]
        }
      )

      pdfMake
        .createPdf(docDefinition)
        .download(
          'Dispositivos_Fecha_' + this.$date.now().format(FMT.simulation)
        )
    },

    resetFilters () {
      this.tableFilters = this.headers.filter(x => x.initialShow)
    }
  },

  mounted () {
    this.$on('addRequested', () => {
      this.isEdit = false
      this.editDispositivo = {}
      this.showDialog = true
    })
    this.maxVisible = this.headers.filter(x => x.initialShow).length - 1
    this.tableFilters = this.headers
      .filter(x => x.text !== 'Acciones' && x.text !== '')
      .slice(0, this.maxVisible)
  }
}
</script>

<style scoped>
.table {
  width: 100vw;
}
.v-data-table {
  overflow: auto;
}
.v-data-table >>> .v-data-table__wrapper {
  overflow: unset;
}
.no-spacing {
  margin: 0;
  padding: 0;
}
.v-input__selections {
  max-height: 20px;
  overflow-y: auto;
}
.v-input__control {
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
}

.v-chip {
  margin-right: 4px;
  max-height: 24px;
}
.mostrar-campos .v-input__selections {
  max-height: 28px;
  overflow-y: hidden;
  display: flex;
  align-items: center;
}

</style>
